import React, { useState } from 'react';
import { ChangeLogResponse } from '../services/api/reviewAutomation.service';

interface ReviewChangesTableProps {
  changes: ChangeLogResponse[];
  loading: boolean;
  selectedChanges: number[];
  onSelectionChange: (changeIds: number[]) => void;
  onReviewChange: (changeId: number, action: 'accept' | 'reject', notes?: string) => void;
  onRefresh: () => void;
}

export const ReviewChangesTable: React.FC<ReviewChangesTableProps> = ({
  changes,
  loading,
  selectedChanges,
  onSelectionChange,
  onReviewChange,
  onRefresh
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [reviewModal, setReviewModal] = useState<{
    changeId: number;
    action: 'accept' | 'reject';
  } | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');

  const toggleRowExpansion = (changeId: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(changeId)) {
      newExpanded.delete(changeId);
    } else {
      newExpanded.add(changeId);
    }
    setExpandedRows(newExpanded);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const pendingChanges = changes.filter((c: ChangeLogResponse) => !c.is_reviewed).map((c: ChangeLogResponse) => c.id);
      onSelectionChange(pendingChanges);
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectChange = (changeId: number, checked: boolean) => {
    if (checked) {
      onSelectionChange([...selectedChanges, changeId]);
    } else {
      onSelectionChange(selectedChanges.filter(id => id !== changeId));
    }
  };

  const openReviewModal = (changeId: number, action: 'accept' | 'reject') => {
    setReviewModal({ changeId, action });
    setReviewNotes('');
  };

  const confirmReview = () => {
    if (reviewModal) {
      onReviewChange(reviewModal.changeId, reviewModal.action, reviewNotes || undefined);
      setReviewModal(null);
      setReviewNotes('');
    }
  };

  const cancelReview = () => {
    setReviewModal(null);
    setReviewNotes('');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getCategoryBadgeColor = (category: string) => {
    const colors: Record<string, string> = {
      'date_changes': 'bg-blue-100 text-blue-800',
      'name_changes': 'bg-green-100 text-green-800',
      'contract_type_changes': 'bg-purple-100 text-purple-800',
      'contact_changes': 'bg-yellow-100 text-yellow-800',
      'supervisor_assignment_changes': 'bg-red-100 text-red-800',
      'other_changes': 'bg-gray-100 text-gray-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const getStatusBadge = (change: ChangeLogResponse) => {
    if (!change.is_reviewed) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          Pending
        </span>
      );
    }
    
    if (change.review_action === 'accept') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Accepted
        </span>
      );
    }
    
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        Rejected
      </span>
    );
  };

  const pendingChanges = changes.filter((c: ChangeLogResponse) => !c.is_reviewed);
  const allPendingSelected = pendingChanges.length > 0 && pendingChanges.every((c: ChangeLogResponse) => selectedChanges.includes(c.id));
  const somePendingSelected = pendingChanges.some((c: ChangeLogResponse) => selectedChanges.includes(c.id));

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-4 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (changes.length === 0) {
    return (
      <div className="bg-white shadow rounded-lg p-6 text-center">
        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">No changes found</h3>
        <p className="mt-1 text-sm text-gray-500">
          No changes match your current filters.
        </p>
        <div className="mt-6">
          <button
            onClick={onRefresh}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Refresh
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">
              Changes ({changes.length})
            </h3>
            <button
              onClick={onRefresh}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={allPendingSelected}
                    ref={(input) => {
                      if (input) input.indeterminate = !allPendingSelected && somePendingSelected;
                    }}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Entity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Affected Fields
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contract
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {changes.map((change) => (
                <React.Fragment key={change.id}>
                  <tr className={selectedChanges.includes(change.id) ? 'bg-blue-50' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedChanges.includes(change.id)}
                        onChange={(e) => handleSelectChange(change.id, e.target.checked)}
                        disabled={change.is_reviewed}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {change.entity_name || `${change.entity_type} #${change.original_entity_id}`}
                          </div>
                          <div className="text-sm text-gray-500">
                            {change.entity_type} • {change.supervisor_name && `Supervisor: ${change.supervisor_name}`}
                          </div>
                          {change.auto_approvable && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 mt-1">
                              Auto-approvable
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {change.categorized_changes.map((fieldChange, index) => (
                          <span
                            key={index}
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getCategoryBadgeColor(fieldChange.category)}`}
                          >
                            {fieldChange.field_name}
                          </span>
                        ))}
                      </div>
                      <button
                        onClick={() => toggleRowExpansion(change.id)}
                        className="text-sm text-blue-600 hover:text-blue-800 mt-1"
                      >
                        {expandedRows.has(change.id) ? 'Hide details' : 'Show details'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {change.contract_name || 'No contract'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(change)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(change.import_timestamp)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {!change.is_reviewed ? (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => openReviewModal(change.id, 'accept')}
                            className="text-green-600 hover:text-green-900"
                          >
                            Accept
                          </button>
                          <button
                            onClick={() => openReviewModal(change.id, 'reject')}
                            className="text-red-600 hover:text-red-900"
                          >
                            Reject
                          </button>
                        </div>
                      ) : (
                        <div className="text-gray-500">
                          {change.review_action}ed by {change.reviewed_by}
                          {change.reviewed_at && (
                            <div className="text-xs">
                              {formatDate(change.reviewed_at)}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                  
                  {/* Expanded row details */}
                  {expandedRows.has(change.id) && (
                    <tr>
                      <td colSpan={7} className="px-6 py-4 bg-gray-50">
                        <div className="space-y-3">
                          <h4 className="text-sm font-medium text-gray-900">Field Changes:</h4>
                          {change.categorized_changes.map((fieldChange, index) => (
                            <div key={index} className="bg-white p-3 rounded border">
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <div className="text-sm font-medium text-gray-900">
                                    {fieldChange.field_name}
                                  </div>
                                  <div className="mt-1 text-sm text-gray-600">
                                    <span className="text-red-600">From:</span> {JSON.stringify(fieldChange.old_value)}
                                  </div>
                                  <div className="text-sm text-gray-600">
                                    <span className="text-green-600">To:</span> {JSON.stringify(fieldChange.new_value)}
                                  </div>
                                </div>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getCategoryBadgeColor(fieldChange.category)}`}>
                                  {fieldChange.category.replace(/_/g, ' ')}
                                </span>
                              </div>
                            </div>
                          ))}
                          
                          {change.review_notes && (
                            <div className="bg-white p-3 rounded border">
                              <h5 className="text-sm font-medium text-gray-900">Review Notes:</h5>
                              <p className="mt-1 text-sm text-gray-600">{change.review_notes}</p>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Review Modal */}
      {reviewModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {reviewModal.action === 'accept' ? 'Accept' : 'Reject'} Change
              </h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (optional)
                </label>
                <textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={`Add a note explaining why you ${reviewModal.action}ed this change...`}
                />
              </div>

              <div className="flex justify-end space-x-2">
                <button
                  onClick={cancelReview}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmReview}
                  className={`px-4 py-2 text-white rounded-md focus:outline-none focus:ring-2 ${
                    reviewModal.action === 'accept'
                      ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                      : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                  }`}
                >
                  {reviewModal.action === 'accept' ? 'Accept' : 'Reject'} Change
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};